import React from 'react';

import styled from 'styled-components';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import InlineYoutubePlayer from 'stile-shared/src/components/2024/common/InlineYoutubePlayer';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

const FeatureSection = styled(Section)`
  margin-bottom: 20px !important;
  .content {
    padding-top: 0 !important;
  }
  @media (max-width: 900px) {
    background: rgba(0, 203, 80, 0.1) !important;
    padding: 0 0 40px !important;
    margin-bottom: 0 !important;
    .content {
      margin: 0 !important;
    }
    .grid-layout {
      margin-top: 0 !important;
    }
  }
  h2 {
    margin-bottom: 80px !important;
    @media (max-width: 900px) {
      margin-bottom: 40px !important;
    }
  }
  .main {
    grid-column: 1 / span 12;
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-gap: 0 26px;
    border-radius: 16px;
    background: rgba(0, 203, 80, 0.1);
    @media (max-width: 900px) {
      background: none;
    }
  }
  .left {
    grid-column: 1 / span 7;
    max-height: 375px;
    @media (max-width: 1220px) and (min-width: 901px) {
      max-height: none;
      height: auto;
    }
    @media (max-width: 900px) {
      grid-column: 1 / span 12;
      max-height: none;
      padding-bottom: 20px;
    }
  }
  .right {
    grid-column: 8 / span 5;
    padding: 32px 32px 32px 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    @media (max-width: 900px) {
      grid-column: 1 / span 12;
      padding: 0 25px;
    }
    h4 {
      margin-bottom: 20px;
      @media (max-width: 900px) {
        margin-bottom: 12px;
      }
    }
    p {
      margin-right: 44px;
      @media (max-width: 900px) {
        margin: 0 0 20px;
      }
    }
    .h7 {
      color: var(--stile-green-3);
      margin-bottom: 4px;
    }
  }
`;

const MediaWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 16px 0 0 16px;
  .video {
    div {
      display: flex !important;
      justify-content: center !important;
    }
    @media (max-width: 1220px) and (min-width: 901px) {
      video {
        width: unset !important;
        max-height: 437px !important;
      }
    }
  }
  /*
   * Case study videos are 16:9, so still images and embedded players have to
   * carry the same ratio to drive an identical panel height. Between 901px and
   * 1220px the local-file video above is sized by height rather than width, so
   * these match with a fixed height and crop horizontally instead.
   */
  .gatsby-image-wrapper,
  .featured-youtube {
    width: 100%;
    aspect-ratio: 16 / 9;

    /*
     * YouTube draws its own title and share overlay on hover, which the US
     * card (a local file in a <video>) never shows. controls, modestbranding
     * and showinfo do not suppress it, so block the pointer instead: hover
     * never reaches the player, and clicks fall through to the card link.
     */
    pointer-events: none;

    img {
      object-fit: cover;
    }

    @media (max-width: 1220px) and (min-width: 901px) {
      aspect-ratio: auto;
      height: 437px;
    }
  }
  @media (max-width: 900px) {
    border-radius: 0;
  }
`;

interface FeaturedCaseStudyProps {
  id?: string;
  title: string;
  subtitle: string;
  description: string;
  url: string;
  /** Video player or image filling the left panel. */
  media: React.ReactNode;
  /** When set, autoplays this video muted and on loop in place of `media`. */
  youtubeUrl?: string;
  buttonLabel?: string;
}

export const FeaturedCaseStudy: React.FC<FeaturedCaseStudyProps> = ({
  id,
  title,
  subtitle,
  description,
  url,
  media,
  youtubeUrl,
  buttonLabel = 'Read full case study',
}) => (
  <FeatureSection id={id}>
    <div className="main">
      <Link className="left" url={url}>
        <MediaWrapper>
          {youtubeUrl ? (
            <div className="featured-youtube">
              <InlineYoutubePlayer url={youtubeUrl} />
            </div>
          ) : (
            media
          )}
        </MediaWrapper>
      </Link>
      <div className="right">
        <div>
          <h4>{title}</h4>
          <h6 className="h7">{subtitle}</h6>
          <p>{description}</p>
        </div>
        <Button style="primary" size="large" href={url} label={buttonLabel} />
      </div>
    </div>
  </FeatureSection>
);

export default FeaturedCaseStudy;
